import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { authService } from '@kss/api';
import { PasswordField } from '../components/PasswordField';

/**
 * Reset-password landing page.
 *
 * The customer arrives here after clicking the link in the email
 * that `/auth/forgot-password` triggered — the URL looks like
 * `/reset-password?token=<base64-jwt>`. We POST `{ token,
 * newPassword }` to `/auth/reset-password` and show a clear
 * confirmation when the backend accepts.
 *
 * The page handles three terminal states:
 *   - missing / malformed token → instruct the user to request a
 *     fresh link
 *   - server rejection (expired / reused / etc.) → surface the
 *     server's error message inline
 *   - success → success screen with a "Sign in" CTA
 */
export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  // The token is single-use and arrives as a URL query param. Some
  // mail clients strip very long tokens — so we also accept it from
  // the `code` shortcut just in case the backend ever reissues
  // shorter ones.
  const token = (searchParams.get('token') ?? searchParams.get('code') ?? '').trim();

  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="mx-auto max-w-md px-6 py-16">
        <Card className="grid gap-3 p-6 text-center">
          <h1 className="text-lg font-bold">Reset link invalid</h1>
          <p className="text-sm text-content-secondary">
            This URL is missing a reset token. Request a fresh link
            from the sign-in screen.
          </p>
          <Link to="/" className="text-sm font-medium text-primary hover:underline">
            ← Back to home
          </Link>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="mx-auto max-w-md px-6 py-16">
        <Card className="grid gap-3 p-6 text-center">
          <h1 className="text-lg font-bold">Password updated</h1>
          <p className="text-sm text-content-secondary">
            You can now sign in with your new password.
          </p>
          <Button
            onClick={() => {
              navigate('/');
              // Defer to next tick so the route swap completes
              // before we open the global login modal.
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('kss:open-login'));
              }, 0);
            }}
          >
            Sign in
          </Button>
        </Card>
      </div>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await authService.resetPassword({
        token,
        newPassword,
        confirmPassword: confirm,
      });
      setDone(true);
      toast.success('Password updated', 'You can now sign in with the new password.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not reset the password.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-md px-6 py-16">
      <Card className="grid gap-4 p-6">
        <div>
          <h1 className="text-lg font-bold">Set a new password</h1>
          <p className="mt-1 text-xs text-content-secondary">
            Choose a password at least 8 characters long.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="grid gap-3">
          {error && <Alert variant="danger">{error}</Alert>}
          <PasswordField
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
            autoFocus
          />
          <PasswordField
            label="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
          />
          <div className="flex justify-end gap-2 pt-1">
            <Link to="/" className="self-center text-xs text-content-secondary hover:underline">
              Cancel
            </Link>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Updating…' : 'Update password'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
